import React, { useState, useEffect, useRef } from 'react';
import { Input, InputGroup } from 'reactstrap';
import * as icons from '../../../assets';
import './DatePicker.scss';

export default function DatePicker({
  type = 'date',
  name,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  className = '',
  placeholder,
  error,
  style = {},
  showIcon = true,
  defaultToNextMonth = false,
  forceTransparentDisabled = false,
  calendarFocus = true,
  ...props
}) {
  // Determine placeholder based on type
  const effectivePlaceholder =
    placeholder ?? (type === 'month' ? 'MM/YYYY' : 'DD/MM/YYYY');

  const [displayValue, setDisplayValue] = useState('');
  const [selectedDate, setSelectedDate] = useState(value || '');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const hiddenDateRef = useRef(null);

  useEffect(() => {
    if (value) {
      setSelectedDate(value);
    } else if (type === 'month' && defaultToNextMonth) {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const isoMonth = `${nextMonth.getFullYear()}-${String(
        nextMonth.getMonth() + 1,
      ).padStart(2, '0')}`;

      setSelectedDate(isoMonth);
      onChange?.(isoMonth);
    } else {
      setSelectedDate('');
    }
  }, [value, type, defaultToNextMonth, onChange]);

  // Display formatters
  const formatToDisplayDate = iso => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };
  const formatToDisplayMonth = iso => {
    if (!iso) return '';
    const [y, m] = iso.split('-');
    return `${m}/${y}`;
  };

  // Input mask formatters
  const formatInputDate = str => {
    const nums = str.replace(/\D/g, '').slice(0, 8).split('');
    return nums.reduce(
      (acc, ch, i) => acc + (i === 2 || i === 4 ? '/' : '') + ch,
      '',
    );
  };
  const formatInputMonth = str => {
    const nums = str.replace(/\D/g, '').slice(0, 6).split('');
    return nums.reduce((acc, ch, i) => acc + (i === 2 ? '/' : '') + ch, '');
  };

  // Mask -> ISO parsers
  const formatToISODate = disp => {
    const digits = disp.replace(/\D/g, '');
    if (digits.length !== 8) return '';
    const d = digits.slice(0, 2);
    const m = digits.slice(2, 4);
    const y = digits.slice(4, 8);
    const dt = new Date(+y, +m - 1, +d);
    if (
      dt.getFullYear() === +y &&
      dt.getMonth() === +m - 1 &&
      dt.getDate() === +d
    ) {
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return '';
  };
  const formatToISOMonth = disp => {
    const digits = disp.replace(/\D/g, '');
    if (digits.length !== 6) return '';
    const m = digits.slice(0, 2).padStart(2, '0');
    const y = digits.slice(2, 6);
    const monthNum = Number(m);
    if (monthNum >= 1 && monthNum <= 12) {
      return `${y}-${m}`;
    }
    return '';
  };

  useEffect(() => {
    if (type === 'month') {
      setDisplayValue(formatToDisplayMonth(selectedDate));
    } else {
      setDisplayValue(formatToDisplayDate(selectedDate));
    }
  }, [selectedDate, type]);

  const handleTextChange = e => {
    const raw = e.target.value;
    const disp =
      type === 'month' ? formatInputMonth(raw) : formatInputDate(raw);
    setDisplayValue(disp);

    const iso =
      type === 'month' ? formatToISOMonth(disp) : formatToISODate(disp);
    if (iso) {
      onChange(iso);
      if (hiddenDateRef.current) hiddenDateRef.current.value = iso;
    } else if (!disp) {
      onChange('');
      if (hiddenDateRef.current) hiddenDateRef.current.value = '';
    }
  };

  // Trigger native picker
  const openCalendar = () => {
    if (disabled || readOnly || !hiddenDateRef.current) return;
    setIsCalendarOpen(true);
    const input = hiddenDateRef.current;
    Object.assign(input.style, {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '38px',
      opacity: 0,
      pointerEvents: 'auto',
      zIndex: 1000,
    });
    input.focus();
    if (input.showPicker) {
      input.showPicker();
    } else {
      input.click();
    }
    setTimeout(() => {
      Object.assign(input.style, {
        width: '1px',
        height: '1px',
        pointerEvents: 'none',
        zIndex: 'auto',
      });
    }, 100);
  };

  // Native picker change
  const handleDateChange = e => {
    const iso = e.target.value;
    setSelectedDate(iso);
    onChange(iso);
    setDisplayValue(
      type === 'month' ? formatToDisplayMonth(iso) : formatToDisplayDate(iso),
    );
  };

  return (
    <div className="datepicker-container" style={{ position: 'relative' }}>
      <InputGroup>
        <Input
          type="text"
          name={name}
          value={displayValue}
          onChange={handleTextChange}
          placeholder={effectivePlaceholder}
          disabled={disabled}
          readOnly={readOnly}
          invalid={error}
          className={`datepicker-input cursor-pointer ${className} ${
            disabled && forceTransparentDisabled
              ? 'force-transparent-disabled'
              : ''
          } ${isCalendarOpen && calendarFocus ? 'calendar-open' : ''}`}
          maxLength={type === 'month' ? 7 : 10}
          pattern={type === 'month' ? '\\d{2}/\\d{4}' : '\\d{2}/\\d{2}/\\d{4}'}
          style={{
            paddingLeft: showIcon ? '35px' : '0.5rem',
            paddingRight: '2.5rem',
            ...style,
            border: props.borderStyle || '1px solid #ced4da',
          }}
          title={props.title}
          onClick={() => {
            // Trigger calendar only on mouse click (not focus)
            if (document.activeElement === hiddenDateRef.current) return;
            openCalendar();
          }}
        />
      </InputGroup>

      {showIcon ? (
        <div
          onClick={openCalendar}
          className="datepicker-icon"
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            pointerEvents: disabled ? 'none' : 'auto',
          }}
        >
          <img src={icons.iconsCalender} height="15px" alt="" />
        </div>
      ) : null}

      <input
        type={type}
        className="hidden-date-input"
        ref={hiddenDateRef}
        value={value || selectedDate || ''}
        onChange={handleDateChange}
        tabIndex={-1}
        title={props.title}
        onBlur={() => setIsCalendarOpen(false)}
      />
    </div>
  );
}
